import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import GoogleAnalytics from '../../analyticsComponents/GoogleAnalytics.js';
import SearchComponent from './SearchComponent';
import GRADES from '../GradePage/grades.json';
import { auth } from '../../config/firebase.js';
import { onAuthStateChanged } from 'firebase/auth';

function Home() {
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => setCurrentUser(user));
    return () => unsub();
  }, []);

  const scrollToGrades = useCallback((e) => {
    e.preventDefault();
    const el = document.getElementById('grades');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <div className="home-page">
      <GoogleAnalytics pages="HomePage" eventType="home_page" />

      <Helmet>
        <title>Share2Teach | Free Open Educational Resources (OERs)</title>
        <meta
          name="description"
          content="Discover and share high-quality Open Educational Resources (OERs) for subjects like Mathematics, English, History, and more on Share2Teach."
        />
        <meta
          name="keywords"
          content="OER, open educational resources, free learning, high school subjects, Share2Teach, online teaching, South Africa education, NWU, north west university, NWU ENTERPRISES"
        />
        <meta name="author" content="Share2Teach Team" />
      </Helmet>

      {/* Hero Section */}
      <div className="relative w-full">
        <div className="relative h-[60vh] md:h-[56vh] overflow-hidden">
          {/* Background image with controlled focal point */}
          <img
            src="/images/homepage-background.jpg"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: 'center 30%' }}
          />
          {/* Soft gradient overlay for readability */}
          <div className="absolute inset-0 bg-black/50" />
          {/* Headings */}
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-4">
            <h1 className="text-4xl md:text-6xl font-bold uppercase tracking-wider text-white drop-shadow-lg hover:scale-105 transition-transform">
              Share2Teach
            </h1>
            <h2 className="mt-2 md:mt-4 text-xl md:text-2xl font-semibold text-gray-200 max-w-3xl">
              Free, teacher-made educational resources to discover, rate, and share.
            </h2>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <button onClick={scrollToGrades} className="inline-flex items-center gap-2 bg-white text-brand-700 font-semibold px-5 py-2.5 rounded-lg shadow hover:shadow-md hover:bg-brand-50 transition-all text-sm">
                Explore grades
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </button>
              {currentUser && (
                <Link to="/contribute" className="inline-flex items-center gap-2 bg-transparent border border-white/70 text-white px-5 py-2.5 rounded-lg hover:bg-white/10 transition-all text-sm font-medium">
                  Contribute a resource
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Find Resources: Search + Grades together ─── */}
      <section
        id="grades"
        className="px-4 md:px-8 max-w-6xl mx-auto mt-10 mb-12 scroll-mt-header"
      >
        {/* Search */}
        <div className="mb-8">
          <SearchComponent />
        </div>

        {/* Or browse by grade */}
        <div className="flex items-center gap-3 mb-5">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800">Or browse by grade</h2>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* Foundation Phase: Grade 1–3 */}
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2.5 px-0.5">Foundation Phase</p>
          <div className="grid grid-cols-3 sm:grid-cols-3 gap-2.5">
            {GRADES.filter(g => ['Grade 1','Grade 2','Grade 3'].includes(g.name)).map((grade) => (
              <Link
                key={grade.name}
                to={`/grade/${grade.name.toLowerCase().replace(/\s+/g, '-')}`}
                state={{ gradeName: grade.name, subjects: grade.subjects }}
                className="group relative rounded-xl p-4 text-center shadow-card hover:shadow-card-hover hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-150 bg-sky-600 text-white"
              >
                <span className="block font-bold text-sm sm:text-base">{grade.name}</span>
                <span className="block text-[11px] text-white/70 mt-0.5 font-medium">{grade.subjects.length} subjects</span>
                <div className="absolute inset-0 rounded-xl bg-white/0 group-hover:bg-white/10 transition-colors duration-150" />
              </Link>
            ))}
          </div>
        </div>

        {/* Intermediate Phase: Grade 4–6 */}
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2.5 px-0.5">Intermediate Phase</p>
          <div className="grid grid-cols-3 sm:grid-cols-3 gap-2.5">
            {GRADES.filter(g => ['Grade 4','Grade 5','Grade 6'].includes(g.name)).map((grade) => (
              <Link
                key={grade.name}
                to={`/grade/${grade.name.toLowerCase().replace(/\s+/g, '-')}`}
                state={{ gradeName: grade.name, subjects: grade.subjects }}
                className="group relative rounded-xl p-4 text-center shadow-card hover:shadow-card-hover hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-150 bg-blue-600 text-white"
              >
                <span className="block font-bold text-sm sm:text-base">{grade.name}</span>
                <span className="block text-[11px] text-white/70 mt-0.5 font-medium">{grade.subjects.length} subjects</span>
                <div className="absolute inset-0 rounded-xl bg-white/0 group-hover:bg-white/10 transition-colors duration-150" />
              </Link>
            ))}
          </div>
        </div>

        {/* Senior Phase: Grade 7–9 */}
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2.5 px-0.5">Senior Phase</p>
          <div className="grid grid-cols-3 sm:grid-cols-3 gap-2.5">
            {GRADES.filter(g => ['Grade 7','Grade 8','Grade 9'].includes(g.name)).map((grade) => (
              <Link
                key={grade.name}
                to={`/grade/${grade.name.toLowerCase().replace(/\s+/g, '-')}`}
                state={{ gradeName: grade.name, subjects: grade.subjects }}
                className="group relative rounded-xl p-4 text-center shadow-card hover:shadow-card-hover hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-150 bg-indigo-600 text-white"
              >
                <span className="block font-bold text-sm sm:text-base">{grade.name}</span>
                <span className="block text-[11px] text-white/70 mt-0.5 font-medium">{grade.subjects.length} subjects</span>
                <div className="absolute inset-0 rounded-xl bg-white/0 group-hover:bg-white/10 transition-colors duration-150" />
              </Link>
            ))}
          </div>
        </div>

        {/* FET Phase: Grade 10–12 */}
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2.5 px-0.5">FET Phase</p>
          <div className="grid grid-cols-3 sm:grid-cols-3 gap-2.5">
            {GRADES.filter(g => ['Grade 10','Grade 11','Grade 12'].includes(g.name)).map((grade) => (
              <Link
                key={grade.name}
                to={`/grade/${grade.name.toLowerCase().replace(/\s+/g, '-')}`}
                state={{ gradeName: grade.name, subjects: grade.subjects }}
                className="group relative rounded-xl p-4 text-center shadow-card hover:shadow-card-hover hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-150 bg-violet-600 text-white"
              >
                <span className="block font-bold text-sm sm:text-base">{grade.name}</span>
                <span className="block text-[11px] text-white/70 mt-0.5 font-medium">{grade.subjects.length} subjects</span>
                <div className="absolute inset-0 rounded-xl bg-white/0 group-hover:bg-white/10 transition-colors duration-150" />
              </Link>
            ))}
          </div>
        </div>

        {/* Teachers */}
        {GRADES.filter(g => g.name === 'Teachers').map((grade) => (
          <div key={grade.name}>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2.5 px-0.5">For Teachers</p>
            <Link
              to={`/grade/${grade.name.toLowerCase().replace(/\s+/g, '-')}`}
              state={{ gradeName: grade.name, subjects: grade.subjects }}
              className="group relative flex items-center justify-center rounded-xl p-4 shadow-card hover:shadow-card-hover hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-150 bg-emerald-600 text-white max-w-xs"
            >
              <span className="block font-bold text-sm sm:text-base">{grade.name}</span>
              <span className="ml-2 text-[11px] text-white/70 font-medium">{grade.subjects.length} subjects</span>
              <div className="absolute inset-0 rounded-xl bg-white/0 group-hover:bg-white/10 transition-colors duration-150" />
            </Link>
          </div>
        ))}
      </section>

      {/* Intro / Benefits */}
      <section className="px-4 md:px-8 max-w-6xl mx-auto mb-10">
        <div className="grid md:grid-cols-3 gap-5">
          <div className="bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-shadow p-6 border border-gray-100">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-brand-50 text-brand-600 mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            </div>
            <h3 className="text-base font-semibold text-gray-900">Discover</h3>
            <p className="text-gray-500 mt-1 text-sm leading-relaxed">Find classroom-ready resources by grade and subject—fast.</p>
          </div>
          <div className="bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-shadow p-6 border border-gray-100">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
            </div>
            <h3 className="text-base font-semibold text-gray-900">Share</h3>
            <p className="text-gray-500 mt-1 text-sm leading-relaxed">Upload your best material and help fellow educators succeed.</p>
          </div>
          <div className="bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-shadow p-6 border border-gray-100">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-amber-50 text-amber-500 mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
            </div>
            <h3 className="text-base font-semibold text-gray-900">Improve</h3>
            <p className="text-gray-500 mt-1 text-sm leading-relaxed">Rate resources and surface the most effective content.</p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="px-4 md:px-8 max-w-6xl mx-auto mb-16">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">How it works</h2>
        <div className="grid md:grid-cols-3 gap-5">
          <div className="bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-shadow p-6 border border-gray-100">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-600 text-white font-bold text-sm mb-3">1</span>
            <h3 className="text-base font-semibold text-gray-900">Explore</h3>
            <p className="text-gray-500 mt-1 text-sm leading-relaxed">Search or browse grades to find relevant resources.</p>
          </div>
          <div className="bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-shadow p-6 border border-gray-100">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-600 text-white font-bold text-sm mb-3">2</span>
            <h3 className="text-base font-semibold text-gray-900">Use &amp; rate</h3>
            <p className="text-gray-500 mt-1 text-sm leading-relaxed">Open a resource, try it in class, and rate its quality.</p>
          </div>
          <div className="bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-shadow p-6 border border-gray-100">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-600 text-white font-bold text-sm mb-3">3</span>
            <h3 className="text-base font-semibold text-gray-900">Share back</h3>
            <p className="text-gray-500 mt-1 text-sm leading-relaxed">Upload your own materials and help others teach better.</p>
          </div>
        </div>
        {currentUser && (
          <div className="mt-6">
            <Link to="/contribute" className="inline-flex items-center gap-2 bg-brand-600 text-white font-semibold px-5 py-2.5 rounded-lg shadow-card hover:bg-brand-700 hover:shadow-card-hover transition-all">
              Start contributing
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
            </Link>
          </div>
        )}
      </section>

    </div>
  );
}

export default Home;
